import { BLUE_600, WHITE } from 'constants/styling/theme'
import { Box, Stack } from '@mui/material'

import { ADJUST_SWATCHES } from './adjust.types'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

interface AdjustSwatchesProps {
  value: string
  onChange: (color: string) => void
  disabled?: boolean
}

/** Brand-safe colours plus a custom colour */
export const AdjustSwatches: FC<AdjustSwatchesProps> = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const isCustom = !ADJUST_SWATCHES.some(color => color.toLowerCase() === value.toLowerCase())

  return (
    <Stack flexDirection="row" flexWrap="wrap" gap="0.8rem" alignItems="center" role="radiogroup" aria-label={t('adjust.color')}>

      {ADJUST_SWATCHES.map((color) => {
        const isSelected = color.toLowerCase() === value.toLowerCase()
        return (
          <Box
            key={color}
            component="button"
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={color}
            disabled={disabled}
            onClick={() => onChange(color)}
            sx={{
              width: '2.4rem',
              height: '2.4rem',
              padding: 0,
              borderRadius: '50%',
              border: '1px solid rgba(19, 24, 29, 0.18)',
              backgroundColor: color,
              cursor: disabled ? 'not-allowed' : 'pointer',
              boxShadow: isSelected ? `0 0 0 2px ${WHITE}, 0 0 0 4px ${BLUE_600}` : 'none',
              '&:focus-visible': { outline: `2px solid ${BLUE_600}`, outlineOffset: 2 },
            }}
          />
        )
      })}

      <Box
        component="input"
        type="color"
        aria-label={t('adjust.custom_color')}
        disabled={disabled}
        value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#ffffff'}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        sx={{
          width: '2.6rem',
          height: '2.6rem',
          padding: 0,
          border: 'none',
          borderRadius: '50%',
          background: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow: isCustom ? `0 0 0 2px ${WHITE}, 0 0 0 4px ${BLUE_600}` : 'none',
          '&::-webkit-color-swatch-wrapper': { padding: 0 },
          '&::-webkit-color-swatch': { border: '1px solid rgba(19, 24, 29, 0.18)', borderRadius: '50%' },
          '&::-moz-color-swatch': { border: '1px solid rgba(19, 24, 29, 0.18)', borderRadius: '50%' },
        }}
      />

    </Stack>
  )
}
